import React, { useEffect, useState } from "react";
import styles from "./Dashboard.module.scss";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../redux/store";
import { updateNewAssessmentVariable } from "../../../redux/slices/globalVariableSlice";
import Navbar from "../../layout/header/Header";
import Pagination from "../../layout/pagination/Pagination";
import { getAllSurveys, patchSurvey, getAllSurveyGroups, generateSurveyGroupPdf, getSurveyGroupPdf } from "../../../api/services/survey-apis";
import 'bootstrap/dist/css/bootstrap.min.css';
import { toast } from "react-toastify";

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const CACHE_EXP_TIME = 10000000;

  const [activeTab, setActiveTab] = useState<'Survey' | 'SurveyGroup'>('Survey');
  const [surveyGroups, setSurveyGroups] = useState<any[]>([]);
  const [hasFetchedGroups, setHasFetchedGroups] = useState(false);
  
  const [search, setSearch] = useState({
    requestor: "",
    company: "",
    surveyName: "",
    status: "",
    email: "" // Added email to search state
  });

  const [groupSearch, setGroupSearch] = useState({
    id: "",
    requestor: "",
    company: "",
    surveyName: ""
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);

  const [groupCurrentPage, setGroupCurrentPage] = useState(1);

  const [AssessmentData, setAssessmentData] = useState<any[]>([]);

  const fetchSurvey = async () => {
    const res = await getAllSurveys();
    setAssessmentData(res.data);
    // console.log("data", res.data);
  };

  const fetchSurveyGroups = async () => {
    try {
      const res = await getAllSurveyGroups();
      setSurveyGroups(res.data);
      setHasFetchedGroups(true);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch Survey Groups");
    }
  };

  useEffect(() => {
    fetchSurvey();
  }, []);

  useEffect(() => {
    if (activeTab === 'SurveyGroup' && !hasFetchedGroups) {
      fetchSurveyGroups();
    }
  }, [activeTab, hasFetchedGroups]);

  useEffect(() => {
    let today = new Date(new Date().toLocaleDateString());
    AssessmentData.map((assessmentDetail: any) => {
      if (assessmentDetail.status === "Initiated") {
        let createdDate = new Date(new Date(assessmentDetail.createdDate).toLocaleDateString());
        var time_difference = today.getTime() - createdDate.getTime();
        var days_difference = time_difference / (1000 * 60 * 60 * 24);
        if (days_difference > 30) {
          assessmentDetail.status = 'Expired';
          patchSurvey(assessmentDetail.surveyId)
            .then(data => {
              // console.log(data);
              toast.success(data.data.message);
            })
            .catch((error) => {
              toast.error(error);
              console.log(error);
            });
        }
        let assignmentCreatedDate = new Date(assessmentDetail.createdDate);
        let validTill = 0;
        let validTillObj = {};
        validTill = assignmentCreatedDate.setDate(assignmentCreatedDate.getDate() + 30);
        validTillObj = { 'validTill': new Date(validTill).toLocaleDateString() };
        Object.assign(assessmentDetail, validTillObj);
      }
    });
  }, [AssessmentData]);

  const [flag, setFlag] = useState(true);

  const handleClick = (item: any) => {
    // console.log(item);
    navigate("/survey-details", { state: { flag: flag, AssessmentDetails: item } });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setSearch(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleGroupSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setGroupSearch(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleGeneratePdf = async (e: React.MouseEvent, surveyGroupId: number) => {
    e.stopPropagation();
    try {
      const res = await generateSurveyGroupPdf(surveyGroupId);
      toast.success(res.data?.message || "PDF Generation Initiated");
      // Refresh list to update status if needed
      fetchSurveyGroups();
    } catch (err) {
      console.error(err);
      toast.error("Failed to generate PDF");
    }
  };

  const handleDownloadPdf = async (e: React.MouseEvent, surveyGroupId: number) => {
    e.stopPropagation();
    try {
      const res = await getSurveyGroupPdf(surveyGroupId);
      if (res.data && (res.data.pdfReady || res.data.isPdfReady) && res.data.pdfUrl) {
        window.open(res.data.pdfUrl, '_blank');
      } else {
        toast.info("PDF not available yet. Please generate it first.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to get PDF URL");
    }
  };

  // Change 1: Ensure search keys match the data keys
  const sortedData = [...AssessmentData].sort((b,a) => new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime());
  // console.log("sorted Items:", sortedData);

  // Change 2: Update search functionality to handle empty search values correctly
  const filteredData = sortedData.filter(item =>
    Object.keys(search).every(key => {
      const itemValue = item[key as keyof typeof item];
      const searchValue = search[key as keyof typeof search];
      return searchValue === "" || (itemValue && itemValue.toString().toLowerCase().includes(searchValue.toLowerCase()));
    })
  );

  // console.log("filter", filteredData);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
  
  // Survey Group filtering
  const sortedGroupData = [...surveyGroups].sort((a, b) => {
    const timeA = new Date(a.createdDate).getTime();
    const timeB = new Date(b.createdDate).getTime();
    if (timeA !== timeB) {
      return timeB - timeA;
    }
    // Secondary sort: ID descending (tie-breaker for same creation time)
    return b.id - a.id;
  });
  
  const filteredGroupData = sortedGroupData.filter(item =>
    Object.keys(groupSearch).every(key => {
      const itemValue = item[key as keyof typeof item];
      const searchValue = groupSearch[key as keyof typeof groupSearch];
      return searchValue === "" || (itemValue && itemValue.toString().toLowerCase().includes(searchValue.toLowerCase()));
    })
  );

  const indexOfLastGroupItem = groupCurrentPage * itemsPerPage;
  const indexOfFirstGroupItem = indexOfLastGroupItem - itemsPerPage;
  const currentGroupItems = filteredGroupData.slice(indexOfFirstGroupItem, indexOfLastGroupItem);

  // console.log("current Items:", currentItems);

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);
  const paginateGroup = (pageNumber: number) => setGroupCurrentPage(pageNumber);

//   return (
//     <div className={styles.dashboard}>
//       <Navbar />
//       <div className={styles.dashboard__page}>
//         <div className={styles.dashboard__page__table__container}>
//           <table>
//             <thead>
//               <tr>                
//                 <td>Requester</td>
//                 <td>Company Name</td>
//                 <td>Survey Name</td>
//                 <td>Receiver</td> {/* Added Email column */}
//                 <td>Status</td>
//               </tr>
//             </thead>
//             <tbody>
//               <tr>
//                 <td colSpan={5}> {/* Updated colSpan to 5 */}
//                   <div className={styles.searchContainer}>
                    
//                     <div>
//                       <input
//                         type="text"
//                         name="requestor"
//                         value={search.requestor}
//                         onChange={handleSearchChange}
//                       />
//                       <i
//                         className="fa-solid fa-magnifying-glass"
//                         style={{
//                           transform: 'translateX(-500%)',
//                         }}
//                       ></i>
//                     </div>
//                     <div>
//                       <input
//                         type="text"
//                         name="company"
//                         value={search.company}
//                         onChange={handleSearchChange}
//                       />
//                       <i
//                         className="fa-solid fa-magnifying-glass"
//                         style={{
//                           transform: 'translateX(-500%)',
//                         }}
//                       ></i>
//                     </div>
//                     <div>
//                       <input
//                         type="text"
//                         name="surveyName" // Changed domain to surveyName
//                         value={search.surveyName} // Changed domain to surveyName
//                         onChange={handleSearchChange}
//                       />
//                       <i
//                         className="fa-solid fa-magnifying-glass"
//                         style={{
//                           transform: 'translateX(-500%)',
//                         }}
//                       ></i>
//                     </div>
//                     <div>
//                       <input
//                         type="text"
//                         name="email" // Added email search input
//                         value={search.email} // Added email search input
//                         onChange={handleSearchChange}
//                       />
//                       <i
//                         className="fa-solid fa-magnifying-glass"
//                         style={{
//                           transform: 'translateX(-500%)',
//                         }}
//                       ></i>
//                     </div>
//                     <div>
//                       <input
//                         type="text"
//                         name="status"
//                         value={search.status}
//                         onChange={handleSearchChange}
//                       />
//                       <i
//                         className="fa-solid fa-magnifying-glass"
//                         style={{
//                           transform: 'translateX(-500%)',
//                         }}
//                       ></i>
//                     </div>
//                   </div>
//                 </td>
//               </tr>
//               {currentItems.map((item: any) => (
//                 <tr
//                   key={item.surveyId}
//                   onClick={() => handleClick(item)}
//                   style={{ cursor: "pointer" }}
//                 >
//                   <td>{item.requestor}</td>
//                   <td>{item.company}</td>
//                   <td>{item.surveyName}</td>
//                   <td>{item.email}</td> {/* Added Email data */}
//                   <td>{item.status}</td>
//                 </tr>
//               ))}
//             </tbody>
//           </table>
//           <Pagination
//             paginate={paginate}
//             projectsPerPage={itemsPerPage}
//             totalProjects={filteredData.length}
//             currentPage={currentPage}
//           />
//         </div>
//       </div>
//     </div>
//   );
// };

// export default Dashboard;

return (
  <div className={styles.dashboard}>
    <Navbar />
    <div className={styles.dashboard__page}>
      <div className="d-flex justify-content-left" style={{ 
        borderBottom: '1px solid #dee2e6' 
      }}>
        <div 
          onClick={() => setActiveTab('Survey')} 
          style={{ 
            padding: '0.5rem 1rem', 
            cursor: 'pointer',
            borderBottom: activeTab === 'Survey' ? '3px solid #0d6efd' : 'none',
            color: activeTab === 'Survey' ? '#0d6efd' : '#6c757d',
            fontWeight: activeTab === 'Survey' ? 'bold' : 'normal',
            marginBottom: '-1px'
          }}
        >
          Survey
        </div>
        <div 
          onClick={() => setActiveTab('SurveyGroup')} 
          style={{ 
            padding: '0.5rem 1rem', 
            cursor: 'pointer',
            borderBottom: activeTab === 'SurveyGroup' ? '3px solid #0d6efd' : 'none',
            color: activeTab === 'SurveyGroup' ? '#0d6efd' : '#6c757d',
            fontWeight: activeTab === 'SurveyGroup' ? 'bold' : 'normal',
            marginBottom: '-1px'
          }}
        >
          Survey Group
        </div>
      </div>
      {activeTab === 'Survey' && (
      <div className={styles.dashboard__page__table__container}>
        <table>
          <thead>
            <tr>
              <td>Requester</td>
              <td>Company Name</td>
              <td>Survey Name</td>
              <td>Receiver</td> {/* Added Email column */}
              <td>Status</td>
            </tr>
          </thead>
          <tbody>
            {/* Search inputs row */}
            <tr>
              <td>
                <div style={{ position: "relative" }}>
                  <input
                    type="text"
                    name="requestor"
                    value={search.requestor}
                    onChange={handleSearchChange}
                    style={{
                      width: "80%", // Smaller width
                      padding: "0.2rem", // Smaller padding
                      border: "1px solid #000", // Thin black border
                      borderRadius: "4px",
                      backgroundColor: "#fff", // Light background
                      color: "#000", // Dark text for contrast
                      textIndent: search.requestor ? "0" : "-9999px", // Show text when typing
                    }}
                  />
                  {!search.requestor && ( // Conditionally render the icon
                    <i
                      className="fa-solid fa-magnifying-glass"
                      style={{
                        position: "absolute",
                        left: "50%",
                        top: "50%",
                        transform: "translate(-50%, -50%)", // Centers the icon
                        color: "#000", // Dark icon for contrast
                      }}
                    ></i>
                  )}
                </div>
              </td>
              <td>
                <div style={{ position: "relative" }}>
                  <input
                    type="text"
                    name="company"
                    value={search.company}
                    onChange={handleSearchChange}
                    style={{
                      width: "80%", // Smaller width
                      padding: "0.2rem", // Smaller padding
                      border: "1px solid #000", // Thin black border
                      borderRadius: "4px",
                      backgroundColor: "#fff", // Light background
                      color: "#000", // Dark text for contrast
                      textIndent: search.company ? "0" : "-9999px", // Show text when typing
                    }}
                  />
                  {!search.company && ( // Conditionally render the icon
                    <i
                      className="fa-solid fa-magnifying-glass"
                      style={{
                        position: "absolute",
                        left: "50%",
                        top: "50%",
                        transform: "translate(-50%, -50%)", // Centers the icon
                        color: "#000", // Dark icon for contrast
                      }}
                    ></i>
                  )}
                </div>
              </td>
              <td>
                <div style={{ position: "relative" }}>
                  <input
                    type="text"
                    name="surveyName"
                    value={search.surveyName}
                    onChange={handleSearchChange}
                    style={{
                      width: "80%", // Smaller width
                      padding: "0.2rem", // Smaller padding
                      border: "1px solid #000", // Thin black border
                      borderRadius: "4px",
                      backgroundColor: "#fff", // Light background
                      color: "#000", // Dark text for contrast
                      textIndent: search.surveyName ? "0" : "-9999px", // Show text when typing
                    }}
                  />
                  {!search.surveyName && ( // Conditionally render the icon
                    <i
                      className="fa-solid fa-magnifying-glass"
                      style={{
                        position: "absolute",
                        left: "50%",
                        top: "50%",
                        transform: "translate(-50%, -50%)", // Centers the icon
                        color: "#000", // Dark icon for contrast
                      }}
                    ></i>
                  )}
                </div>
              </td>
              <td>
                <div style={{ position: "relative" }}>
                  <input
                    type="text"
                    name="email"
                    value={search.email}
                    onChange={handleSearchChange}
                    style={{
                      width: "80%", // Smaller width
                      padding: "0.2rem", // Smaller padding
                      border: "1px solid #000", // Thin black border
                      borderRadius: "4px",
                      backgroundColor: "#fff", // Light background
                      color: "#000", // Dark text for contrast
                      textIndent: search.email ? "0" : "-9999px", // Show text when typing
                    }}
                  />
                  {!search.email && ( // Conditionally render the icon
                    <i
                      className="fa-solid fa-magnifying-glass"
                      style={{
                        position: "absolute",
                        left: "50%",
                        top: "50%",
                        transform: "translate(-50%, -50%)", // Centers the icon
                        color: "#000", // Dark icon for contrast
                      }}
                    ></i>
                  )}
                </div>
              </td>
              <td>
                <div style={{ position: "relative" }}>
                  <input
                    type="text"
                    name="status"
                    value={search.status}
                    onChange={handleSearchChange}
                    style={{
                      width: "80%", // Smaller width
                      padding: "0.2rem", // Smaller padding
                      border: "1px solid #000", // Thin black border
                      borderRadius: "4px",
                      backgroundColor: "#fff", // Light background
                      color: "#000", // Dark text for contrast
                      textIndent: search.status ? "0" : "-9999px", // Show text when typing
                    }}
                  />
                  {!search.status && ( // Conditionally render the icon
                    <i
                      className="fa-solid fa-magnifying-glass"
                      style={{
                        position: "absolute",
                        left: "50%",
                        top: "50%",
                        transform: "translate(-50%, -50%)", // Centers the icon
                        color: "#000", // Dark icon for contrast
                      }}
                    ></i>
                  )}
                </div>
              </td>
            </tr>

            {/* Data rows */}
            {currentItems.map((item: any) => (
              <tr
                key={item.surveyId}
                onClick={() => handleClick(item)}
                style={{ cursor: "pointer" }}
              >
                <td>{item.requestor}</td>
                <td>{item.company}</td>
                <td>{item.surveyName}</td>
                <td>{item.email}</td> {/* Added Email data */}
                <td>{item.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination
          paginate={paginate}
          projectsPerPage={itemsPerPage}
          totalProjects={filteredData.length}
          currentPage={currentPage}
        />
      </div>
      )}

      {activeTab === 'SurveyGroup' && (
      <div className={styles.dashboard__page__table__container}>
          <table>
            <thead>
              <tr>
                <td>ID</td>
                <td>Requestor</td>
                <td>Company</td>
                <td>Survey Name</td>
                <td>Created Date</td>
                <td>Actions</td>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <div style={{ position: "relative" }}>
                    <input type="text" name="id" value={groupSearch.id} onChange={handleGroupSearchChange} style={{ width: "80%", padding: "0.2rem", border: "1px solid #000", borderRadius: "4px", backgroundColor: "#fff", color: "#000", textIndent: groupSearch.id ? "0" : "-9999px" }} />
                    {!groupSearch.id && <i className="fa-solid fa-magnifying-glass" style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%, -50%)", color: "#000" }}></i>}
                  </div>
                </td>
                <td>
                  <div style={{ position: "relative" }}>
                    <input type="text" name="requestor" value={groupSearch.requestor} onChange={handleGroupSearchChange} style={{ width: "80%", padding: "0.2rem", border: "1px solid #000", borderRadius: "4px", backgroundColor: "#fff", color: "#000", textIndent: groupSearch.requestor ? "0" : "-9999px" }} />
                    {!groupSearch.requestor && <i className="fa-solid fa-magnifying-glass" style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%, -50%)", color: "#000" }}></i>}
                  </div>
                </td>
                <td>
                  <div style={{ position: "relative" }}>
                    <input type="text" name="company" value={groupSearch.company} onChange={handleGroupSearchChange} style={{ width: "80%", padding: "0.2rem", border: "1px solid #000", borderRadius: "4px", backgroundColor: "#fff", color: "#000", textIndent: groupSearch.company ? "0" : "-9999px" }} />
                    {!groupSearch.company && <i className="fa-solid fa-magnifying-glass" style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%, -50%)", color: "#000" }}></i>}
                  </div>
                </td>
                <td>
                  <div style={{ position: "relative" }}>
                    <input type="text" name="surveyName" value={groupSearch.surveyName} onChange={handleGroupSearchChange} style={{ width: "80%", padding: "0.2rem", border: "1px solid #000", borderRadius: "4px", backgroundColor: "#fff", color: "#000", textIndent: groupSearch.surveyName ? "0" : "-9999px" }} />
                    {!groupSearch.surveyName && <i className="fa-solid fa-magnifying-glass" style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%, -50%)", color: "#000" }}></i>}
                  </div>
                </td>
                <td></td>
                <td></td>
              </tr>
              {currentGroupItems.map((item: any) => (
                <tr key={item.id} style={{ height: '40px' }}>
                  <td style={{ padding: '4px' }}>{item.id}</td>
                  <td style={{ padding: '4px' }}>{item.requestor}</td>
                  <td style={{ padding: '4px' }}>{item.company}</td>
                  <td style={{ padding: '4px' }}>{item.surveyName}</td>
                  <td style={{ padding: '4px' }}>{item.createdDate}</td>
                  <td style={{ padding: '4px' }}>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button 
                        className="btn btn-sm btn-outline-primary"
                        onClick={(e) => handleGeneratePdf(e, item.id)}
                        title="Generate PDF"
                        style={{ padding: '0.2rem 0.5rem', fontSize: '0.8rem' }}
                      >
                         <i className="fa-solid fa-rotate-right"></i>
                      </button>
                      <button 
                        className="btn btn-sm btn-outline-success"
                        onClick={(e) => handleDownloadPdf(e, item.id)}
                        title="View PDF"
                        style={{ padding: '0.2rem 0.5rem', fontSize: '0.8rem' }}
                      >
                         <i className="fa-solid fa-eye"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            paginate={paginateGroup}
            projectsPerPage={itemsPerPage}
            totalProjects={filteredGroupData.length}
            currentPage={groupCurrentPage}
          />
      </div>
      )}

    </div>
  </div>
);
};

export default Dashboard;
